import { type GitWriteRepository } from '@apm/contracts';
import { useQuery } from '@tanstack/react-query';
import { GitBranchPlus, GitPullRequestCreate } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../components/ui/button';
import { gitApi, gitKeys } from '../api';
import { type GitDevelopmentSlotProps } from '../types';
import { GitWriteDialog, type GitWriteMode } from './git-write-dialog';

/**
 * Câu nói **vì sao** chưa ghi được — đi kèm nút mờ (§8.4 "Lưu ý": nói cần scope /
 * vai gì, không phải một 500). Khoá i18n theo lý do, và theo loại máy chủ với
 * `read_only` — GitLab và GitHub gọi quyền ghi bằng hai bộ chữ khác nhau.
 */
export function gitWriteBlockedKey(repository: GitWriteRepository): string | null {
  switch (repository.blockedReason) {
    case null:
      return null;
    case 'read_only':
      return `gitTab.write.blocked.read_only.${repository.hostKind}`;
    default:
      return `gitTab.write.blocked.${repository.blockedReason}`;
  }
}

/**
 * Nút **Tạo nhánh** · **Tạo merge request** của khối "Phát triển" — APM-GIT-k,
 * cắm vào `GIT_DEVELOPMENT_SLOTS` (docs/plan-git-integration.md §8.2, §8.4).
 *
 * Chỉ hiện với `git:act` và khi đang xem "bây giờ". Dự án chưa gắn kho nào thì
 * không hiện gì — nút "Sao chép tên nhánh" ở trên đã là đường làm tay. Có kho mà
 * không kho nào ghi được ⇒ hai nút **mờ** kèm câu nói thiếu gì.
 *
 * ⚠️ Không nhận asOf: thao tác ghi tác động lên "bây giờ" — đang xem quá khứ
 * (`asOf` có giá trị) thì không render và không phát lời gọi nào.
 */
export function GitWriteActions({
  projectId,
  target,
  branchName,
  links,
  asOf,
  permissions,
}: GitDevelopmentSlotProps): ReactNode {
  const { t } = useTranslation();
  const [mode, setMode] = useState<GitWriteMode | null>(null);
  const enabled = permissions.act && asOf === undefined;

  const options = useQuery({
    queryKey: gitKeys.writeOptions(projectId, target),
    queryFn: () => gitApi.writeOptions(projectId, target),
    enabled,
    staleTime: 60_000,
    retry: false,
  });

  // Lỗi đọc (quyền vừa đổi, mạng) ⇒ im lặng: hai nút là phần phụ của khối.
  if (!enabled || !options.data || options.data.repositories.length === 0) return null;

  const repositories = options.data.repositories;
  const canWrite = repositories.some((repository) => repository.writable);
  const blockedKey = canWrite ? null : gitWriteBlockedKey(repositories[0]!);

  /** Nhánh đang tồn tại của việc này ở một kho — nhánh nguồn mặc định của MR. */
  const sourceBranchOf = (repositoryId: string): string | null =>
    links.branches.find(
      (branch) => branch.repositoryId === repositoryId && branch.deletedAt === null,
    )?.name ?? null;

  return (
    <div className="flex flex-col gap-1" data-testid="git-write-actions">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!canWrite}
          onClick={() => setMode('branch')}
        >
          <GitBranchPlus aria-hidden />
          {t('gitTab.write.createBranch')}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={!canWrite}
          onClick={() => setMode('changeRequest')}
        >
          <GitPullRequestCreate aria-hidden />
          {t('gitTab.write.createChangeRequest')}
        </Button>
      </div>
      {blockedKey ? (
        <p className="text-[11px] text-muted-foreground" data-testid="git-write-blocked">
          {t(blockedKey)}
        </p>
      ) : null}
      {mode ? (
        <GitWriteDialog
          mode={mode}
          projectId={projectId}
          target={target}
          options={options.data}
          branchName={branchName}
          sourceBranchOf={sourceBranchOf}
          onClose={() => setMode(null)}
        />
      ) : null}
    </div>
  );
}
